
export const SEARCH_LIMIT = 300;

export function formatFileSize(bytes) {
	if (!bytes || bytes <= 0) return '0 B';
	const k = 1000;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
	let value = bytes / Math.pow(k, i);
	const unit = sizes[i];
	if (unit === 'MB') value = value >= 10 ? Math.round(value) : parseFloat(value.toFixed(1));
	else value = parseFloat(value.toFixed(1));
	return value + ' ' + unit;
}

export function escapeHtml(text) {
	// DEBUG: Log UTF-8 encoding check for German umlauts
	if (typeof text === 'string' && text.toLowerCase().includes('zus') && text.toLowerCase().includes('tzliche')) {
		console.log('[escapeHtml] UTF-8 DEBUG - Input:', {
			text: text,
			textLength: text.length,
			charAt3: text.charAt(3),
			charAt4: text.charAt(4),
			charAt5: text.charAt(5),
			charCodeAt3: text.charCodeAt(3),
			charCodeAt4: text.charCodeAt(4),
			charCodeAt5: text.charCodeAt(5),
			includesA: text.includes('Ä'),
			includesAumlaut: text.includes('\u00C4'),
			bytes: Array.from(new TextEncoder().encode(text)).slice(0, 20)
		});
	}
	
	const div = document.createElement('div');
	div.textContent = text;
	const result = div.innerHTML;
	
	// DEBUG: Log UTF-8 encoding check after textContent
	if (typeof text === 'string' && text.toLowerCase().includes('zus') && text.toLowerCase().includes('tzliche')) {
		console.log('[escapeHtml] UTF-8 DEBUG - Output:', {
			result: result,
			resultLength: result.length,
			includesA: result.includes('Ä'),
			includesAumlaut: result.includes('\u00C4'),
			includesAEntity: result.includes('&Auml;'),
			includesAHex: result.includes('&#196;'),
			textContentValue: div.textContent
		});
	}
	
	return result;
}

export function detectMeta(filePath, fileName) {
	const full = ((filePath ? (filePath + '/' + fileName) : fileName) || '').replace(/\\/g, '/');
	const U = full.toUpperCase();
	let type = '';
	if (/(^|\/)PAB(\/|$)|\bPAB\b|\bБПО\b/.test(U)) type = 'PAB';
	else if (/HASIPL/.test(U)) type = 'HASIPL';
	else if (/BPL/.test(U)) type = 'BPL';
	else if (/BTB|ТБП/.test(U)) type = 'BTB';
	else if (/\bEDS?\b|SECED|INTLRHED|HCOEXECLETTER|\bИД\b|INTLRHED/.test(U)) type = 'ED';
	else if (/FLAG ORDER|\bFO\b/.test(U)) type = 'FO';
	else if (/HCO\s?B|БОХС|HCOINFOBUL?LETIN/.test(U)) type = 'HCOB';
	else if (/HCOPL|HCO\s?PLS?|ИП\s?ОХС/.test(U)) type = 'HCOPL';
	else if (/LECTURE|\bLECT\b|ЛЕКЦ|ACC\b|TAPE|ADMINTAPE/.test(U)) type = 'LECT';
	else if (/\bBOOKS?\b|КНИГИ/.test(U)) type = 'BOOK';
	else if (/\bJOURNAL\b|\/JOURNALS\//.test(U)) type = 'JOURNAL';
	else if (/\bCOURSE\b|COURSES/.test(U)) type = 'COURSE';
	else if (/\bDICT\b|DICTIONAR/.test(U)) type = 'DICT';
	else if (/\bVOLUME\b/.test(U)) type = 'VOLUME';
	else type = 'OTHER';
	
	let lang = '';
	if (/\bENGLISH\b/.test(U)) lang = 'EN';
	else if (/\bRUSSIAN\b|\bРУС\b/.test(U)) lang = 'RU';
	else if (/\bGERMAN\b|\bDEUTSCH\b|\bDE\b/.test(U)) lang = 'DE';
	else if (/\bFRENCH\b|\bFRANZ\b|\bFR\b/.test(U)) lang = 'FR';
	else if (/\bSPANISH\b|\bESPA\b|\bES\b/.test(U)) lang = 'ES';

	const ext = (fileName.includes('.') ? fileName.split('.').pop() : '').toUpperCase();

	let format = 'text';
	if (/\/(AUDIO)\//.test(U)) format = 'audio';
	else if (/\/(VIDEO)\//.test(U)) format = 'video';
	else if (/\/(SCAN)\//.test(U)) format = 'scan';
	else if (/\/(TEXT)\//.test(U)) format = 'text';

	return { type, lang, ext, format };
}

export function filterFiles(filesList, filters = {}) {
	const { extensions = [], minSize = null, maxSize = null, pathPattern = null, excludePatterns = [] } = filters;
	return filesList.filter(file => {
		if (extensions.length > 0) {
			const ext = String(file.n || '').split('.').pop().toLowerCase();
			if (!extensions.includes(ext)) return false;
		}
		if (minSize !== null && file.s < minSize) return false;
		if (maxSize !== null && file.s > maxSize) return false;
		if (pathPattern && !(new RegExp(pathPattern)).test((file.p || '') )) return false;
		for (const pattern of excludePatterns) if ((new RegExp(pattern)).test((file.p || '') + '/' + (file.n || ''))) return false;
		return true;
	});
}

export function sortFiles(filesList, sortBy = 'name', order = 'asc') {
	const sorted = [...filesList].sort((a, b) => {
		let compareValue = 0;
		switch (sortBy) {
			case 'name': compareValue = (a.n || '').localeCompare(b.n || ''); break;
			case 'size': compareValue = (a.s || 0) - (b.s || 0); break;
			case 'path': compareValue = (a.p || '').localeCompare(b.p || ''); break;
			default: compareValue = 0;
		}
		return order === 'asc' ? compareValue : -compareValue;
	});
	return sorted;
}

// Encode file path for URL - encodes each segment separately while preserving slashes
// This properly handles special characters like single quotes, parentheses, etc.
// Note: encodeURIComponent() does NOT encode single quote ('), so we explicitly replace it
export function encodeFilePath(path) {
	if (!path) return '';
	// Split by slashes, encode each segment, then join back
	// Explicitly replace single quote with %27 because encodeURIComponent doesn't encode it
	return path.split('/').map(segment => encodeURIComponent(segment).replace(/'/g, '%27')).join('/');
}


